import { Component, OnInit } from '@angular/core';
import { OnboardingService } from 'src/app/services/onboarding.service';
import { OnboardingClient } from 'src/app/shared/models/onboarding/onboarding-client';
import { ClientListResponse } from 'src/app/shared/models/onboarding/client-list-response';
import { SettingsService } from 'src/app/services/settings.service';
import { UserService } from 'src/app/services/user.service';
import { UserFullDetails } from 'src/app/shared/models/users/user-full-details';
import { Note } from 'src/app/shared/models/note/note';
import { HttpErrorResponse } from '@angular/common/http';
import { SelectOption } from 'src/app/shared/models/general/select-option';

@Component({
  selector: 'app-onboarding',
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.scss'],
})
export class OnboardingComponent implements OnInit {
  showCardBody: boolean = true;
  showOriginal: boolean = false;
  isCollapsed = false;
  showError: boolean = false;
  errorResponse: HttpErrorResponse;
  selectedUserFullDetails: UserFullDetails;
  selectedClientState: number = ClientState.Onboarding;

  loadingProfile = false;
  loadingClientList = false;
  infoLoading: boolean = false;
  showUserProfileModal = false;

  selectedStepId: number | null = null;
  filterableClients: OnboardingClient[] = [];
  clients: OnboardingClient[] = [];

  clientListResponse: ClientListResponse = new ClientListResponse();

  searchText: string = '';

  selectedClientId: number | null;

  //TODO: need to get this from the logged in user
  createdByUserId: number | null;

  onboardingClient: OnboardingClient;

  clientStateOptions: SelectOption[] = [
    { label: 'Onboarding', value: ClientState.Onboarding },
    { label: 'Onboarded', value: ClientState.Onboarded },
    { label: 'Archived', value: ClientState.Archived },
  ];

  buildClientCountString(): string {
    return `${ClientState[this.selectedClientState]}: ${this.filterableClients.length}`;
  }

  getStateBadge(): { [key: string]: boolean } {
    const clientState = ClientState[this.selectedClientState];
    return {
      'badge-primary': clientState === 'Onboarding',
      'badge-info': clientState === 'Onboarded',
      'badge-dark': clientState === 'Archived',
    };
  }

  constructor(
    private onboardingService: OnboardingService,
    private settingsService: SettingsService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.createdByUserId = this.settingsService.user.id;
    this.LoadOnboardingClients(false);
  }

  onClientSelect(clientId: number) {
    this.isCollapsed = true;
    this.selectedClientId = clientId;
    this.GetOnboardingClientInformation();
  }

  onGridCollapse() {
    this.isCollapsed = !this.isCollapsed;
    this.selectedClientId = null;
  }

  onEditProfileClick() {
    this.GetUserFullDetails(this.selectedClientId);
  }

  archiveClient() {
    this.loadingClientList = true;
    this.onboardingService.ArchiveAndUnonboard(this.selectedClientId, this.createdByUserId).subscribe({
      next: () => {
        this.removeClientFromArray();
        this.isCollapsed = false;
        this.selectedClientId = null;
        this.loadingClientList = false;
      },
      error: (error) => {
        this.loadingClientList = false;
        this.parseAndDisplayError(error);
      },
    });
  }

  onStateFilterChange() {
    switch (this.selectedClientState) {
      case 1:
        this.GetArchivedClients();
        break;
      case 2:
        this.LoadOnboardingClients(true);
        break;
      default:
        this.LoadOnboardingClients(false);
        break;
    }
  }

  filterByStepId() {
    if (this.selectedStepId === null) {
      this.filterableClients = this.clientListResponse.clients;
      return;
    }

    this.filterableClients = this.clientListResponse.clients.filter((client) => client.nextStepId === this.selectedStepId);
  }

  canComplete() {
    return (
      this.onboardingClient.totalStepsCount === this.onboardingClient.completedStepsCount && !this.onboardingClient.isOnboardingCompleted
    );
  }

  canRestart() {
    return this.onboardingClient.isOnboardingCompleted || !this.onboardingClient.isActive;
  }

  completeOnboardingProcess() {
    this.loadingClientList = true;
    this.onboardingService.CompleteOnboarding(this.selectedClientId, this.createdByUserId).subscribe({
      next: () => {
        this.removeClientFromArray();
        this.isCollapsed = false;
        this.selectedClientId = null;
        this.loadingClientList = false;
      },
      error: (error) => {
        this.loadingClientList = false;
        this.parseAndDisplayError(error);
      },
    });
  }

  restartOnboardingProcess() {
    this.loadingClientList = true;
    this.onboardingService.RestartOnboarding(this.selectedClientId, this.createdByUserId).subscribe({
      next: () => {
        this.removeClientFromArray();
        this.isCollapsed = false;
        this.selectedClientId = null;
        this.loadingClientList = false;
      },
      error: (error) => {
        this.loadingClientList = false;
        this.parseAndDisplayError(error);
      },
    });
  }

  private GetArchivedClients() {
    this.loadingClientList = true;
    this.clientListResponse = null;
    this.onboardingService.GetArchivedClients().subscribe({
      next: (response: ClientListResponse) => {
        this.clientListResponse = response;
        this.filterableClients = this.clientListResponse.clients;
        this.loadingClientList = false;
      },
      error: (error) => {
        this.loadingClientList = false;
        this.parseAndDisplayError(error);
      },
    });
  }

  private LoadOnboardingClients(onlyOnboardedCompleted: boolean) {
    this.loadingClientList = true;
    this.clientListResponse = null;
    this.onboardingService.GetOnboardingClients(onlyOnboardedCompleted).subscribe({
      next: (response: ClientListResponse) => {
        this.clientListResponse = response;
        this.filterableClients = this.clientListResponse.clients;
        this.loadingClientList = false;
      },
      error: (error) => {
        this.loadingClientList = false;
        this.parseAndDisplayError(error);
      },
    });
  }

  private GetUserFullDetails(userId: number) {
    this.loadingProfile = true;
    this.userService.GetUserDetails(userId, true).subscribe({
      next: (fullDetails: UserFullDetails) => {
        this.showUserProfileModal = true;
        this.selectedUserFullDetails = fullDetails;
        this.loadingProfile = false;
        this.showError = false;
      },
      error: (error) => {
        this.loadingProfile = false;
        this.parseAndDisplayError(error);
      },
    });
  }

  private GetOnboardingClientInformation() {
    this.infoLoading = true;
    this.onboardingService.GetOnboardingClientInformation(this.selectedClientId).subscribe({
      next: (response: OnboardingClient) => {
        this.onboardingClient = response;
        this.infoLoading = false;
      },
      error: (error) => {
        this.infoLoading = false;
        this.parseAndDisplayError(error);
      },
    });
  }

  parseAndDisplayError(error: any) {
    this.errorResponse = error;
    this.showError = true;
  }

  newNote: Note;

  handleNoteCreated(note: Note) {
    this.newNote = note;
  }

  handleUserProfileUpdated(userFullDetails: UserFullDetails) {
    if (userFullDetails.user.active == false) {
      this.removeClientFromArray();
      this.isCollapsed = false;
      this.selectedClientId = null;
    }
  }

  private removeClientFromArray() {
    this.clientListResponse.clients.splice(
      this.clientListResponse.clients.findIndex((client) => client.userId === this.selectedClientId),
      1
    );
    this.filterableClients = this.clientListResponse.clients;
  }
}

enum ClientState {
  Onboarding = 0,
  Archived = 1,
  Onboarded = 2,
}
